import React, { useState, useEffect } from "react";
import { useFormikContext, FieldArray } from "formik";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { FaTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { QuotationFormValues } from "../initialValues";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchTermsAndConditionByDropDown } from "slices/Masters/terms-and-condition/thunk";
import type { RootState } from "../../../../index";

const TermsConditionsSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<
    QuotationFormValues | ServiceQuotationFormValues
  >();
  const dispatch = useDispatch();
  const { termsAndConditionsList, loading } = useSelector(
    (state: RootState) => state.TermsAndCondition,
  );

  const [newTerm, setNewTerm] = useState({
    type: "",
    option: "",
  });

  // Process API data to get unique types and options map
  const [termTypeOptions, setTermTypeOptions] = useState<string[]>([]);
  const [termOptionMap, setTermOptionMap] = useState<Record<string, string[]>>(
    {},
  );

  // Ensure terms_and_conditions is always an array
  const termsAndConditions = Array.isArray(values.terms_and_conditions)
    ? values.terms_and_conditions
    : [];

  // Initialize terms_and_conditions as empty array if it's not an array
  useEffect(() => {
    if (!Array.isArray(values.terms_and_conditions)) {
      setFieldValue("terms_and_conditions", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch terms and conditions data on component mount
  useEffect(() => {
    dispatch(fetchTermsAndConditionByDropDown() as any);
  }, [dispatch]);

  // Process API data when it changes
  useEffect(() => {
    if (termsAndConditionsList && termsAndConditionsList.length > 0) {
      // Extract unique types
      const uniqueTypes: string[] = Array.from(
        new Set(termsAndConditionsList.map((item: any) => item.type as string)),
      );
      setTermTypeOptions(uniqueTypes);

      // Create type to options mapping
      const optionsMap: Record<string, string[]> = {};
      uniqueTypes.forEach((type) => {
        const optionsForType = termsAndConditionsList
          .filter((item: any) => item.type === type)
          .map((item: any) => item.options);
        optionsMap[type] = optionsForType;
      });
      setTermOptionMap(optionsMap);
    }
  }, [termsAndConditionsList]);

  const handleAddTerm = () => {
    if (!newTerm.type || !newTerm.option) {
      return;
    }

    // Ensure we're working with an array
    const currentTerms = Array.isArray(values.terms_and_conditions)
      ? values.terms_and_conditions
      : [];

    // Check if a term with this type already exists
    const existingTermIndex = currentTerms.findIndex(
      (t) => t.type === newTerm.type,
    );

    if (existingTermIndex >= 0) {
      const updatedTerms = [...currentTerms];
      const existingTerm = currentTerms[existingTermIndex];

      updatedTerms[existingTermIndex] = {
        ...existingTerm,
        description: newTerm.option,
        option: newTerm.option,
      };
      setFieldValue("terms_and_conditions", updatedTerms);
    } else {
      const term = {
        id: Date.now().toString(),
        type: newTerm.type,
        option: newTerm.option,
        description: newTerm.option,
      };

      setFieldValue("terms_and_conditions", [...currentTerms, term]);
    }

    setNewTerm({ type: "", option: "" });
  };

  const getAvailableOptions = () => {
    if (!newTerm.type) return [];
    return termOptionMap[newTerm.type] || [];
  };

  // Helper to get options from a term (handles both old and new format)
  const getTermOptions = (term: any): string[] => {
    if (term.description && term.description.includes("\n")) {
      // New format: options stored as newline-separated
      return term.description.split("\n").filter(Boolean);
    }
    // Old format: single description
    return term.description ? [term.description] : [];
  };

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
        <h5 className="fw-semibold">Terms & Conditions</h5>
      </div>

      <div
        className="rounded"
        // style={{ backgroundColor: MecordColors?.LightGrayColor }}
      >
        {/* Add Term Form */}
        <Row className="g-3 mb-3">
          <Col xs={12} sm={12} md={4}>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Autocomplete
                options={termTypeOptions}
                getOptionLabel={(option: any) => option}
                isOptionEqualToValue={(option: any, value: any) =>
                  option === value
                }
                value={newTerm.type || null}
                loading={loading}
                disabled={loading}
                onChange={(_: any, newValue: any) => {
                  const currentTerms = Array.isArray(
                    values.terms_and_conditions,
                  )
                    ? values.terms_and_conditions
                    : [];
                  const existingTerm = currentTerms.find(
                    (t: any) => t.type === newValue,
                  );
                  const existingOptions = existingTerm
                    ? getTermOptions(existingTerm)
                    : [];

                  setNewTerm({
                    type: newValue || "",
                    option: existingOptions[0] || "",
                  });
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest}>
                      {option}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder={loading ? "Loading..." : "Select Type"}
                    size="small"
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={12} md={8}>
            <Form.Group>
              <Form.Label>Option</Form.Label>
              <Autocomplete
                options={getAvailableOptions()}
                getOptionLabel={(option: any) => option}
                isOptionEqualToValue={(option: any, value: any) =>
                  option === value
                }
                value={newTerm.option || null}
                disabled={!newTerm.type}
                onChange={(_: any, newValue: any) => {
                  setNewTerm({ ...newTerm, option: newValue || "" });
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest}>
                      {option}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Option"
                    size="small"
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col xs={12} className="d-flex justify-content-end align-items-end">
            <Button
              variant="danger"
              size="sm"
              onClick={handleAddTerm}
              disabled={!newTerm.type || !newTerm.option}
              style={{
                backgroundColor: MecordColors.ThemeBGColor,
                borderColor: MecordColors.ThemeBGColor,
                minWidth: "80px",
              }}
            >
              + Add
            </Button>
          </Col>
        </Row>

        {/* Terms List */}
        <FieldArray name="terms_and_conditions">
          {({ remove }) => (
            <div>
              {termsAndConditions.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {termsAndConditions.map((term, index) => {
                    const options = getTermOptions(term);
                    return (
                      <div
                        key={term.id}
                        className="p-3 border rounded position-relative"
                        style={{ backgroundColor: "#fff" }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="w-100">
                            <h6 className="fw-bold mb-2">{term.type}</h6>
                            {options.length > 0 ? (
                              <ul
                                className="mb-0 ps-3"
                                style={{ fontSize: "14px" }}
                              >
                                {options.map((opt, optIndex) => (
                                  <li
                                    key={optIndex}
                                    style={{ marginBottom: "4px" }}
                                  >
                                    {opt}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div style={{ fontSize: "14px" }}>
                                {term.description}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="btn btn-sm p-1 ms-2"
                            style={{
                              backgroundColor: "#ffe5e5",
                              color: "#dc3545",
                              border: "none",
                              borderRadius: "4px",
                              minWidth: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FaTrashAlt size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  No terms and conditions added yet.
                </div>
              )}
            </div>
          )}
        </FieldArray>
      </div>
    </div>
  );
};

export default TermsConditionsSection;
